import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  Autocomplete,
  Chip,
  Tooltip,
  Switch,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  Add,
  Delete,
  Save,
  ProductionQuantityLimits,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

interface UserItemLimit {
  id: number;
  user_id: number;
  item_id: number;
  item_name?: string;
  monthly_limit: number;
}

interface AvailableItem {
  id: number;
  name: string;
}

interface UserInfo {
  id: number;
  name: string;
  email: string;
  role: string;
}

const UserItemLimits: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const userId = parseInt(id || '0');
  const { showSuccess, showError, confirm } = useNotification();

  const [user, setUser] = useState<UserInfo | null>(null);
  const [limits, setLimits] = useState<UserItemLimit[]>([]);
  const [availableItems, setAvailableItems] = useState<AvailableItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // New limit form
  const [selectedItem, setSelectedItem] = useState<AvailableItem | null>(null);
  const [monthlyLimit, setMonthlyLimit] = useState<number | ''>('');

  useEffect(() => {
    fetchData();
  }, [userId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [userRes, limitsRes, itemsRes] = await Promise.all([
        api.get(`/api/users/${userId}`),
        api.get(`/api/users/${userId}/item-limits`),
        api.get('/api/items'),
      ]);

      setUser(userRes.data);
      setLimits(limitsRes.data.limits || []);
      setAvailableItems(itemsRes.data.items || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getAvailableItemsForAdd = (): AvailableItem[] => {
    const existingItemIds = limits.map((l) => l.item_id);
    return availableItems.filter((item) => !existingItemIds.includes(item.id));
  };

  const handleAddLimit = async () => {
    if (!selectedItem || monthlyLimit === '') return;

    setSaving(true);
    setError(null);
    try {
      const response = await api.post('/api/user-item-limits', {
        user_id: userId,
        item_id: selectedItem.id,
        monthly_limit: monthlyLimit,
      });

      setLimits([...limits, { ...response.data, item_name: selectedItem.name }]);
      setSelectedItem(null);
      setMonthlyLimit('');
      setSuccess('Item limit added successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message|| 'Failed to add item limit');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateLimit = async (limit: UserItemLimit, newLimit: number) => {
    try {
      await api.put(`/api/user-item-limits/${limit.id}`, {
        monthly_limit: newLimit,
      });

      setLimits(
        limits.map((l) =>
          l.id === limit.id ? { ...l, monthly_limit: newLimit } : l
        )
      );
      setSuccess('Limit updated');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err: any) {
      setError(err.message|| 'Failed to update limit');
    }
  };

  const handleDeleteLimit = async (limit: UserItemLimit) => {
    const confirmed = await confirm({
      title: 'Remove Limit',
      message: `Remove limit for "${limit.item_name}"? This will allow unlimited ordering.`,
      confirmText: 'Remove',
      cancelText: 'Cancel',
      confirmColor: 'error',
      icon: 'warning',
    });
    if (!confirmed) return;

    try {
      await api.delete(`/api/user-item-limits/${limit.id}`);
      setLimits(limits.filter((l) => l.id !== limit.id));
      showSuccess('Limit removed');
    } catch (err: any) {
      showError(err.message|| 'Failed to delete limit');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/users')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <ProductionQuantityLimits sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Item Sale Capping
          </Typography>
          {user && (
            <Typography variant="body2" color="text.secondary">
              {user.name} ({user.email})
            </Typography>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Add New Limit */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Add Item Limit
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Set monthly quantity limits for items. Set to 0 to block ordering completely.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <Autocomplete
            sx={{ minWidth: 300, flex: 1 }}
            options={getAvailableItemsForAdd()}
            getOptionLabel={(option) => option.name}
            value={selectedItem}
            onChange={(_, newValue) => setSelectedItem(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Item"
                placeholder="Search items..."
                size="small"
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Typography variant="body2">{option.name}</Typography>
              </li>
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />

          <TextField
            label="Monthly Limit"
            type="number"
            size="small"
            value={monthlyLimit}
            onChange={(e) => setMonthlyLimit(e.target.value === '' ? '' : parseInt(e.target.value))}
            placeholder="e.g., 100"
            inputProps={{ min: 0 }}
            sx={{ width: 150 }}
            helperText="0 = Blocked"
          />

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddLimit}
            disabled={!selectedItem || monthlyLimit === '' || saving}
          >
            {saving ? 'Adding...' : 'Add Limit'}
          </Button>
        </Box>
      </Paper>

      {/* Existing Limits */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Current Item Limits
        </Typography>

        {limits.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No item limits set. User can order any quantity of all items.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Item Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Monthly Limit</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {limits.map((limit) => (
                  <TableRow key={limit.id} hover>
                    <TableCell>
                      <Typography fontWeight="medium">{limit.item_name}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        type="number"
                        size="small"
                        value={limit.monthly_limit}
                        onChange={(e) => {
                          const newVal = parseInt(e.target.value) || 0;
                          handleUpdateLimit(limit, newVal);
                        }}
                        inputProps={{ min: 0, style: { textAlign: 'center', width: 80 } }}
                        sx={{ width: 100 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {limit.monthly_limit === 0 ? (
                        <Chip label="Blocked" color="error" size="small" />
                      ) : (
                        <Chip label={`Max ${limit.monthly_limit}/month`} color="primary" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Remove Limit">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteLimit(limit)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => navigate('/users')}>
            Back to Users
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default UserItemLimits;
